// src/components/TsunamiMeter.js
// Real-time "Rumble" widget for EVENTS — the Rumble Meter.
//
// A Rumble (evergreen 3.3) is a real-money purchase from CritCash that boosts a
// live event. Once the event's results are final, CritCash credits a share to
// the verified players who played; the Rumble is refunded if the event is
// cancelled, nobody in it can receive, or it never finishes (server:
// functions/modules/rumbles.js). Streams and squads use StreamRumble; the wire
// contract for all three targets lives in utils/rumblePayment.js.
//
// Fan-facing copy is MONEY-FREE: the meter shows Rumble counts, wave tiers and
// progress to the next burst, never an amount. Amounts appear only in the
// payer's own picker. When the meter crosses a milestone it RUMBLES: the
// screen shakes and the King Crit dice rain down.
//
// Payment happens ON-SCREEN so the viewer is never pulled off the live stream:
//  - useRumbleSetup() -> getFloodPaymentSetup({targetType:'event', targetId})
//    says whether Rumbles are open for this viewer + event (tipsEnabled), the
//    picker range and presets, and whether the viewer has a saved card.
//  - hasSavedCard  -> sendRumbleOneTap() charges off-session (one tap).
//  - first time / SCA / no card -> createRumbleCheckout() returns a clientSecret
//    we mount as a Stripe EMBEDDED checkout in a floating overlay; completing
//    it SAVES the card so the next Rumble is one-tap.
//
// Purchase limits (STEP3_5A_TIERS_DESIGN 3.7, 5.14): the warnings render only
// from 'limits'-mode data (setup.limit, a tips-limit refusal's details.limit,
// the card-limit / card-blocked tokens, cardCheck: true on a checkout). In
// 'cap' mode the server sends none of it and the picker is unchanged.

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom'; // RumbleBurst portals its overlay to <body>
import {
  View,
  Text,
  Pressable,
  TextInput,
  StyleSheet,
  Animated,
  Dimensions,
  Modal,
  ActivityIndicator,
  Alert,
  Platform,
  Image,
  ScrollView,
} from 'react-native';
import { db } from '../../firebaseConfig';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  doc,
  collection,
  onSnapshot,
  query,
  orderBy,
  limit,
  getDoc,
} from 'firebase/firestore';
import {
  rumbleWave,
  waveLabel,
  RUMBLE_WAVES,
  getRumbleSetup,
  sendRumbleOneTap,
  createRumbleCheckout,
  mountEmbeddedCheckout,
  rumbleErrorKind,
  pollTipPurchaseOutcome,
} from '../utils/rumblePayment';
import {
  limitLines,
  cardLine,
  captureOutcomeLine,
  cardStatusFromError,
  limitFromError,
  savedCardStatus,
  CONFIRMING_TEXT,
  STILL_CONFIRMING_TEXT,
} from '../utils/spendingLimit';
import SpendingLimitNotice from './SpendingLimitNotice';

// King Crit dice for the Rumble burst (green + purple, faces 1 & 20).
const DIE_G1 = require('../../assets/1_gdice.webp');
const DIE_P1 = require('../../assets/1_pdice.webp');
const DIE_G20 = require('../../assets/20_gdice.webp');
const DIE_P20 = require('../../assets/20_pdice.webp');
// RN Web returns a URL string (or { default/uri }) from require() — normalize.
const resolveSrc = (m) => (m && (m.uri || m.default)) || m;
const RUMBLE_DICE = [DIE_G1, DIE_P1, DIE_G20, DIE_P20].map(resolveSrc);

// Gold King Crit die used as the Rumble brand mark (replaces the wave glyph).
const DIE_ICON = require('../../assets/20_dice.webp');
const DieIcon = ({ size = 18 }) => (
  <Image source={DIE_ICON} style={{ width: size, height: size }} resizeMode="contain" />
);

// Color-code a recent Rumble's die by its wave (loot-tier feel):
// ripple = green, wave/surge = purple, tsunami = gold.
function getRumbleDie(waveId) {
  if (waveId === 'tsunami') return DIE_ICON;                  // gold — biggest
  if (waveId === 'surge' || waveId === 'wave') return DIE_P20; // purple
  return DIE_G20;                                              // green — ripple
}

const { width: SCREEN_WIDTH } = Dimensions.get('window');

const IS_WEB = Platform.OS === 'web';

// Dollar label for the payer's OWN picker only (never a fan-facing surface).
function formatUsd(cents) {
  const dollars = (Number(cents) || 0) / 100;
  return `$${Number.isInteger(dollars) ? dollars : dollars.toFixed(2)}`;
}

function positiveOr(v, fallback) {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

// SVG Wave Icon Component
const WaveIconSVG = ({ size = 18, color = '#00bfff' }) => (
  <View style={{ width: size, height: size, justifyContent: 'center', alignItems: 'center' }}>
    {Platform.OS === 'web' ? (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path d="M2 12C2 12 5 8 8 8C11 8 13 16 16 16C19 16 22 12 22 12" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
        <path d="M2 18C2 18 5 14 8 14C11 14 13 22 16 22C19 22 22 18 22 18" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
      </svg>
    ) : (
      <Text style={{ color, fontSize: size, fontWeight: '700' }}>~</Text>
    )}
  </View>
);

// Rumble tiers (in cents) — the METER "rumbles" (screen shake + dice fall) each
// time the cumulative Rumble total crosses one of these. Doubling ladder. The
// meter shows them as LEVELS (never as amounts): below the first = Level 1.
const TSUNAMI_MILESTONES = [
  2500, 5000, 10000, 20000, 40000, 80000, 160000, 320000,
];

// Money-free meter level for a total (or for a milestone a burst claimed).
export function rumbleMeterLevel(totalCents) {
  const n = Number(totalCents) || 0;
  return TSUNAMI_MILESTONES.filter((m) => m <= n).length + 1;
}

// Event meter = legacy flood fields + 3.3 Rumble fields (design 3.3 §10). 3.3
// never increments floodTotalCents (only the kept legacy webhook branch can,
// for a pre-deploy checkout), so the two are summed, never either alone.
export function eventRumbleTotals(data) {
  return {
    totalCents: (Number(data?.floodTotalCents) || 0) + (Number(data?.rumbleTotalCents) || 0),
    count: (Number(data?.floodCount) || 0) + (Number(data?.rumbleCount) || 0),
  };
}

// Feed rows (floodContributions): 3.3 rows carry only the wave id (no amount);
// legacy flood rows carry amountCents, mapped onto the same ladder so they
// still render — as a wave, never as an amount.
const WAVE_IDS = new Set(RUMBLE_WAVES.map((w) => w.id));
export function rumbleRowWave(row) {
  const id = String(row?.wave || '').toLowerCase();
  return WAVE_IDS.has(id) ? id : rumbleWave(row?.amountCents);
}

// One burst per milestone per key per page session, no matter how many
// watchers observe the crossing. The key is an event id, or a namespaced key
// for other meters (StreamRumble uses `stream:<sessionId>`). EventWatchScreen
// runs a screen-level watcher (alive through every ceremony phase — the meter
// itself only mounts inside the Rumble modal / expanded sidebar, which is why
// the dice never fell over the ceremony), and an open meter watches too;
// whichever sees the crossing first claims it here. A total that moves
// BACKWARDS (admin reset) re-arms the key so test drives can fire the same
// milestone again.
const _burstClaimed = new Map(); // key -> highest milestone already burst
export function claimRumbleMilestone(key, prevTotal, newTotal) {
  if (!key) return null;
  if (newTotal < prevTotal) {
    // The caller WATCHED the total drop — that only happens on an admin reset.
    // Re-arm so a later climb bursts again. (A merely-stale lower observation
    // is an increase from ITS prev, so it lands in the dedupe below instead.)
    _burstClaimed.delete(key);
    return null;
  }
  const prevM = TSUNAMI_MILESTONES.filter((m) => m <= prevTotal).pop() || 0;
  const newM = TSUNAMI_MILESTONES.filter((m) => m <= newTotal).pop() || 0;
  if (newM <= prevM) return null;
  if (newM <= (_burstClaimed.get(key) || 0)) return null;
  _burstClaimed.set(key, newM);
  return newM;
}

// Quick amounts (in cents) until setup answers; the server's presetsCents
// (already lifted to the event minimum and capped at today's allowance) win.
const QUICK_AMOUNTS = [100, 500, 1000];

// Fallback limits until getFloodPaymentSetup loads (backend is authoritative).
// $500 is the per-purchase maximum for one Rumble; the 24-hour limit is the
// server's, and setup's maxCents already applies it.
const DEFAULT_MIN_CENTS = 100;    // $1
const DEFAULT_MAX_CENTS = 50000;  // $500

// Wave colors (the ladder itself lives in rumblePayment.js).
const WAVE_COLORS = { tsunami: '#FCD000', surge: '#00bfff', wave: '#4ade80', ripple: '#888' };

// Payer-facing copy for refusals. Money-free; the server's own sentences are
// shown after any leading lowercase code token (tips-limit:,
// account-under-review:, ...) is stripped. rumbleErrorKind still classifies
// on the raw tips-* tokens.
const LIMIT_TEXT = 'You’ve reached today’s limit for Super Crits and Rumbles.';
const CLOSED_TEXT = 'Rumbles aren’t open for this event right now.';
const PENDING_TEXT = 'We couldn’t confirm your Rumble yet. Please wait a minute before sending another.';
const FAILED_TEXT = 'Could not send your Rumble. Please try again.';
const CHECKOUT_FAILED_TEXT = 'Could not start checkout. Please try again.';

function rumbleNoticeText(err, kind) {
  const raw = String(err?.message || '').trim();
  const stripped = raw.replace(/^[a-z][a-z-]*:\s*/, '').trim();
  if (kind === 'pending') return raw.startsWith('tips-pending') && stripped ? stripped : PENDING_TEXT;
  if (kind === 'limit') return stripped || LIMIT_TEXT;
  if (kind === 'unavailable') return stripped || CLOSED_TEXT;
  // Other server refusals are sentences (e.g. the picker range); bare codes
  // such as 'internal' never reach the payer.
  return /\s/.test(stripped) ? stripped : FAILED_TEXT;
}

// Why the picker is hidden, from setup (reason is a fan sentence, or the
// 'tips-limit' token when today's allowance is below the minimum).
function closedReasonFor(setup) {
  if (setup && setup.enabled === false) return 'Rumbles are coming soon.';
  if (setup?.reason === 'tips-limit') return LIMIT_TEXT;
  if (typeof setup?.reason === 'string' && setup.reason.trim()) return setup.reason.trim();
  return CLOSED_TEXT;
}

function setupIsOpen(setup) {
  // A usable publishable key is required too — if Stripe is enabled but the
  // key isn't configured, the embedded checkout can't mount.
  return !!(setup && setup.enabled === true && setup.tipsEnabled === true
    && typeof setup.publishableKey === 'string' && setup.publishableKey);
}

// Send locks, per target, kept at module level so they outlive the meter:
// EventWatch mounts one meter in the Rumble modal and one in the sidebar, and
// closing either unmounts it while its send keeps running. `inFlight` holds
// while a send runs; `pendingUntil` holds for a minute after an unknown
// one-tap outcome (the card may have been charged; the webhook backstop
// records it). Every meter for the target refuses a new send while either
// holds (mirrors StreamRumble's busyRef + PENDING_LOCK_MS).
const PENDING_LOCK_MS = 60000;
const _sendLocks = new Map(); // 'targetType:targetId' -> { inFlight, pendingUntil }
function rumbleLockKey(target) {
  return target && target.targetType && target.targetId ? `${target.targetType}:${target.targetId}` : '';
}
function rumbleSendLock(key) {
  let lock = _sendLocks.get(key);
  if (!lock) {
    lock = { inFlight: false, pendingUntil: 0 };
    _sendLocks.set(key, lock);
  }
  return lock;
}
function rumblePendingLocked(key) {
  const lock = _sendLocks.get(key);
  return !!lock && Date.now() < lock.pendingUntil;
}
function rumbleSendBlocked(key) {
  const lock = _sendLocks.get(key);
  return !!lock && (lock.inFlight || Date.now() < lock.pendingUntil);
}

// ============================================
// RUMBLE SETUP — is the picker open for this viewer + target?
// ============================================
// Logged-in viewers ask the server: getFloodPaymentSetup runs the same target
// validator as the charge, so a flag-off, closed, finished or unreceivable
// target comes back tipsEnabled:false (with a reason). A logged-out viewer
// can't call setup (auth required); they get the sign-up funnel only while the
// target's flag is on (settings/streams, world-readable, explicit true) and
// the caller says the target is live (opts.loggedOutLive). `refreshKey`
// re-fetches — EventWatch passes the event status, so the button hides the
// moment an event completes. A null target does nothing (display-only).
export function useRumbleSetup(target, refreshKey, opts = {}) {
  const targetType = (target && target.targetType) || null;
  const targetId = (target && target.targetId) || null;
  const loggedOutLive = !!(opts && opts.loggedOutLive);

  // Wait for Firebase Auth to restore the session before choosing a path, or
  // a logged-in viewer would flash the logged-out funnel on every load.
  const [authState, setAuthState] = useState({ ready: false, uid: null });
  useEffect(() => onAuthStateChanged(getAuth(), (u) => {
    setAuthState({ ready: true, uid: u ? u.uid : null });
  }), []);
  const { ready: authReady, uid } = authState;

  // A stored reply only counts for the viewer + target it was fetched for, so
  // navigating to another event (or switching accounts) never reuses it.
  const key = `${uid || ''}|${targetType || ''}|${targetId || ''}`;
  const [state, setState] = useState({ key: null, setup: null, loading: false, flagOn: false });
  const reqRef = useRef(0); // drops out-of-order replies

  const refresh = useCallback(async () => {
    const req = ++reqRef.current;
    const reqKey = `${uid || ''}|${targetType || ''}|${targetId || ''}`;
    if (!IS_WEB || !targetType || !targetId) {
      setState({ key: reqKey, setup: null, loading: false, flagOn: false });
      return;
    }
    if (!authReady) return;
    // Same viewer + target: keep the last reply on screen while re-reading.
    setState((s) => (s.key === reqKey ? { ...s, loading: true } : { key: reqKey, setup: null, loading: true, flagOn: false }));
    if (!uid) {
      try {
        const snap = await getDoc(doc(db, 'settings', 'streams'));
        if (req !== reqRef.current) return;
        const field = targetType === 'event' ? 'eventRumblesEnabled' : 'streamRumblesEnabled';
        setState({ key: reqKey, setup: null, loading: false, flagOn: snap.exists() && snap.data()?.[field] === true });
      } catch (e) {
        if (req === reqRef.current) setState({ key: reqKey, setup: null, loading: false, flagOn: false });
      }
      return;
    }
    try {
      const data = await getRumbleSetup({ targetType, targetId });
      if (req !== reqRef.current) return;
      setState({ key: reqKey, setup: data || null, loading: false, flagOn: false });
    } catch (err) {
      if (req !== reqRef.current) return;
      // Master Stripe off / transport error: treat as not open (not an error).
      console.warn('TsunamiMeter: getFloodPaymentSetup failed:', err?.code || err?.message);
      setState({ key: reqKey, setup: null, loading: false, flagOn: false });
    }
  }, [authReady, uid, targetType, targetId]);

  useEffect(() => { refresh(); }, [refresh, refreshKey]);

  const memoTarget = useMemo(
    () => (targetType && targetId ? { targetType, targetId } : null),
    [targetType, targetId],
  );
  const current = state.key === key;
  const setup = current ? state.setup : null;
  const loggedIn = !!uid;
  const open = !!memoTarget && current
    && (loggedIn ? setupIsOpen(setup) : (authReady && state.flagOn && loggedOutLive));
  return {
    target: memoTarget,
    setup,
    loading: !!memoTarget && (!authReady || !current || state.loading),
    loggedIn,
    open,
    refresh,
  };
}

// Time ago formatter
function timeAgo(date) {
  const seconds = Math.floor((new Date() - date) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}

// ============================================
// RUMBLE BURST — screen shake + falling King Crit dice on a milestone
// ============================================
let _rumbleStylesInjected = false;
function injectRumbleStyles() {
  if (_rumbleStylesInjected || !IS_WEB || typeof document === 'undefined') return;
  _rumbleStylesInjected = true;
  const css = `
  @keyframes ccRumbleShake {
    0%,100% { transform: translate(0,0) rotate(0); }
    10% { transform: translate(-7px,4px) rotate(-0.6deg); }
    20% { transform: translate(8px,-5px) rotate(0.6deg); }
    30% { transform: translate(-9px,6px) rotate(-0.8deg); }
    40% { transform: translate(9px,-4px) rotate(0.7deg); }
    50% { transform: translate(-6px,5px) rotate(-0.5deg); }
    60% { transform: translate(7px,-6px) rotate(0.5deg); }
    70% { transform: translate(-5px,3px) rotate(-0.4deg); }
    80% { transform: translate(4px,-3px) rotate(0.3deg); }
    90% { transform: translate(-3px,2px) rotate(-0.2deg); }
  }
  .cc-rumble-shake { animation: ccRumbleShake 0.7s cubic-bezier(.36,.07,.19,.97) both; }
  .cc-rumble-overlay { position: fixed; inset: 0; pointer-events: none; z-index: 99999; overflow: hidden; }
  .cc-rumble-die { position: absolute; top: -14vh; will-change: transform; filter: drop-shadow(0 6px 10px rgba(0,0,0,0.5)); animation-name: ccRumbleFall; animation-timing-function: cubic-bezier(.45,.05,.55,.95); animation-iteration-count: 1; animation-fill-mode: both; }
  @keyframes ccRumbleFall {
    0% { transform: translateY(-16vh) rotate(0deg); opacity: 0; }
    8% { opacity: 1; }
    100% { transform: translateY(118vh) rotate(var(--cc-rot,540deg)); opacity: 1; }
  }
  .cc-rumble-banner { position: fixed; top: 16%; left: 0; right: 0; text-align: center; z-index: 100000; pointer-events: none; }
  @media (prefers-reduced-motion: reduce) {
    .cc-rumble-shake { animation: none; }
    .cc-rumble-die { display: none; }
  }`;
  const el = document.createElement('style');
  el.textContent = css;
  document.head.appendChild(el);
}

// Deterministic-enough dice scatter (computed once; reused each burst).
const RUMBLE_PIECES = Array.from({ length: 16 }, (_, i) => ({
  src: RUMBLE_DICE[i % RUMBLE_DICE.length],
  left: Math.round(Math.random() * 94) + '%',
  size: 34 + Math.round(Math.random() * 46),         // 34–80px
  delay: (Math.random() * 0.5).toFixed(2),
  dur: (1.6 + Math.random() * 1.2).toFixed(2),        // 1.6–2.8s
  rot: (Math.random() < 0.5 ? -1 : 1) * (360 + Math.round(Math.random() * 540)),
}));

export const RumbleBurst = ({ visible, milestone, onDone, title, subtitle }) => {
  useEffect(() => {
    if (!visible || !IS_WEB || typeof document === 'undefined') return;
    injectRumbleStyles();
    // While EventWatch has element-fullscreened #video-column, body isn't
    // painted — shake (and portal, below) into the fullscreen element instead.
    const target = document.fullscreenElement || document.webkitFullscreenElement || document.body;
    target.classList.add('cc-rumble-shake');
    const tShake = setTimeout(() => target.classList.remove('cc-rumble-shake'), 750);
    const tDone = setTimeout(() => onDone && onDone(), 2800);
    return () => {
      clearTimeout(tShake);
      clearTimeout(tDone);
      target.classList.remove('cc-rumble-shake');
    };
  }, [visible]);

  if (!visible || !IS_WEB || typeof document === 'undefined') return null;
  const overlay = (
    <div className="cc-rumble-overlay">
      {RUMBLE_PIECES.map((p, i) => (
        <img
          key={i}
          src={p.src}
          alt=""
          className="cc-rumble-die"
          style={{
            left: p.left, width: p.size, height: p.size,
            animationDelay: p.delay + 's', animationDuration: p.dur + 's',
            '--cc-rot': p.rot + 'deg',
          }}
        />
      ))}
      <div className="cc-rumble-banner">
        <span style={{ fontSize: 44, fontWeight: 900, color: '#FCD000', textShadow: '0 0 18px rgba(252,208,0,0.85)', letterSpacing: 1 }}>{title || 'RUMBLE!'}</span>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#fff', marginTop: 6 }}>
          {/* Money-free default (fan-facing): never the milestone amount. */}
          {subtitle != null ? subtitle : 'The crowd is rumbling!'}
        </div>
      </div>
    </div>
  );
  // Portal to <body>: the burst must stack above the WHOLE page. Rendered
  // inline it inherits its mount point's stacking/clipping context — inside
  // Header (overflow:hidden wrap) the dice rained BEHIND the create screen's
  // panels (founder repro 2026-07-13). document.body + z-index 99999 wins
  // everywhere, for every consumer (Header, Subscription, Tsunami, EventWatch).
  // Exception: during element-fullscreen only the fullscreened subtree paints,
  // so the burst must portal INTO it or the finale dice are invisible.
  const portalHost = document.fullscreenElement || document.webkitFullscreenElement || document.body;
  return ReactDOM.createPortal(overlay, portalHost);
};

// ============================================
// RECENT CONTRIBUTION ITEM
// ============================================
const ContributionItem = ({ contribution, isNew }) => {
  const slideAnim = useRef(new Animated.Value(isNew ? -20 : 0)).current;
  const opacityAnim = useRef(new Animated.Value(isNew ? 0 : 1)).current;
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    if (isNew) {
      Animated.parallel([
        Animated.timing(slideAnim, {
          toValue: 0,
          duration: 300,
          useNativeDriver: true,
        }),
        Animated.timing(opacityAnim, {
          toValue: 1,
          duration: 300,
          useNativeDriver: true,
        }),
      ]).start();
    }
  }, [isNew]);

  // Show the rumbler's PUBLIC PROFILE name (+ photo), not the synthetic
  // "User<uid>" fallback stored on the contribution.
  useEffect(() => {
    let active = true;
    const uid = contribution.userId;
    if (!uid) { setProfile(null); return; }
    getDoc(doc(db, 'public_profiles', uid))
      .then((snap) => { if (active && snap.exists()) setProfile(snap.data()); })
      .catch(() => {});
    return () => { active = false; };
  }, [contribution.userId]);

  const waveId = rumbleRowWave(contribution);
  const createdAt = contribution.createdAt?.toDate?.() || contribution.createdAt || new Date();
  const name = contribution.anonymous
    ? 'Anonymous'
    : (profile?.userName || contribution.displayName || 'A viewer');
  const photo = contribution.anonymous ? null : (profile?.profilePhotoUrl || null);

  return (
    <Animated.View
      style={[
        styles.contributionItem,
        {
          transform: [{ translateX: slideAnim }],
          opacity: opacityAnim,
        },
      ]}
    >
      <Image source={getRumbleDie(waveId)} style={styles.contributionDie} resizeMode="contain" />
      {photo ? (
        <Image source={{ uri: photo }} style={styles.contributionAvatar} />
      ) : null}
      <Text style={styles.contributionUser} numberOfLines={1}>
        {name}
      </Text>
      {/* The wave, never the amount (legacy rows map amountCents to a wave). */}
      <Text style={[styles.contributionAmount, { color: WAVE_COLORS[waveId] || WAVE_COLORS.ripple }]}>
        {waveLabel(waveId)} Rumble
      </Text>
      <Text style={styles.contributionTime}>{timeAgo(createdAt)}</Text>
    </Animated.View>
  );
};

// ============================================
// MAIN RUMBLE METER COMPONENT
// ============================================
export default function TsunamiMeter({
  eventId,
  collection: collectionProp,
  compact = false,
  showContributions = true,
  onContribute,
  onRequireAuth,
  onVerify,
  rumble: rumbleProp,
}) {
  // Rumble setup. EventWatchScreen passes its screen-level useRumbleSetup()
  // result so the compact-bar button, this modal and the sidebar agree; a
  // meter mounted on its own fetches for {targetType:'event', targetId}.
  const ownRumble = useRumbleSetup(
    !rumbleProp && eventId ? { targetType: 'event', targetId: eventId } : null,
  );
  const rumble = rumbleProp || ownRumble;
  const { setup, loading: setupLoading, loggedIn, open: rumblesOpen, target: rumbleTarget } = rumble;
  // The screen owns the setup when it passes `rumble`, so a send that settles
  // after this meter closed still re-reads it (today's allowance moved).
  const parentOwnsSetup = !!rumbleProp;
  const lockKey = rumbleLockKey(rumbleTarget);

  // Callbacks are read through refs, so nothing below re-runs (or remounts the
  // Stripe form) when a parent re-renders with a fresh inline function —
  // EventWatch re-renders every second during a ceremony (useEventCeremony's
  // heartbeat), which used to tear down the checkout while the payer typed.
  const onContributeRef = useRef(onContribute);
  const onRequireAuthRef = useRef(onRequireAuth);
  const refreshSetupRef = useRef(rumble.refresh);
  onContributeRef.current = onContribute;
  onRequireAuthRef.current = onRequireAuth;
  refreshSetupRef.current = rumble.refresh;

  // Meter state (combined flood + Rumble fields, cents; shown only as a count
  // and levels — never as an amount)
  const [meterTotal, setMeterTotal] = useState(0);
  const [meterCount, setMeterCount] = useState(0);
  const [recentContributions, setRecentContributions] = useState([]);
  const [customAmount, setCustomAmount] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const processingRef = useRef(false); // double-tap guard (state lags a render)
  // Bumps on unmount and on a target switch: a send that settles after either
  // sets no state here and never opens a checkout nobody would see.
  const sendSeqRef = useRef(0);
  const mountedRef = useRef(false); // only the spinner reset reads this
  const [notice, setNotice] = useState(null); // { tone: 'ok'|'info'|'error', text }
  // Purchase limits ('limits' mode only; all stay null in 'cap' mode):
  const [limitErr, setLimitErr] = useState(null);       // { limit, amountCents } from a tips-limit refusal
  const [cardRefusal, setCardRefusal] = useState(null); // { status, amountCents } from a one-tap refusal
  const [outcomeLine, setOutcomeLine] = useState(null); // L7 / L8 / L8b after a held checkout
  const [showRumbleBurst, setShowRumbleBurst] = useState(false);
  const [rumbleMilestone, setRumbleMilestone] = useState(0);
  const [newContributionIds, setNewContributionIds] = useState(new Set());
  const seenContributionIdsRef = useRef(null);

  // Embedded Stripe checkout (first-time / SCA / no saved card)
  // { clientSecret, publishableKey } | null
  const [embedded, setEmbedded] = useState(null);
  const checkoutContainerRef = useRef(null);
  const checkoutCompleteRef = useRef(null);
  const checkoutErrorRef = useRef(null);

  // Animation refs
  const pulseAnim = useRef(new Animated.Value(1)).current;
  const progressAnim = useRef(new Animated.Value(0)).current;

  // Track previous total for milestone detection
  const prevTotalRef = useRef(0);

  // A meter (re)opened within a minute of an unknown one-tap outcome says so
  // up front; the cleanup retires any send still running for this target.
  useEffect(() => {
    if (lockKey && rumblePendingLocked(lockKey)) setNotice({ tone: 'info', text: PENDING_TEXT });
    return () => {
      sendSeqRef.current += 1;
      setLimitErr(null);
      setCardRefusal(null);
      setOutcomeLine(null);
    };
  }, [lockKey]);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Level progress toward the next burst (money-free: levels + percent only).
  const currentMilestone = TSUNAMI_MILESTONES.filter(m => m <= meterTotal).pop() || 0;
  const nextMilestone = TSUNAMI_MILESTONES.find(m => m > meterTotal) || currentMilestone * 2;
  const prevMilestone = currentMilestone;
  const progressPercent = nextMilestone > prevMilestone
    ? Math.max(0, Math.min(((meterTotal - prevMilestone) / (nextMilestone - prevMilestone)) * 100, 100))
    : 0;
  const meterLevel = rumbleMeterLevel(meterTotal);

  // Track which collection the event is in
  const [foundCollection, setFoundCollection] = useState(collectionProp || null);

  // First: Find which collection the event is in (one-time check)
  useEffect(() => {
    if (!eventId || collectionProp) return;

    const findCollection = async () => {
      const collections = ['matches', 'tournaments', 'events'];

      for (const collectionName of collections) {
        try {
          const docRef = doc(db, collectionName, eventId);
          const snap = await getDoc(docRef);

          if (snap.exists()) {
            setFoundCollection(collectionName);
            return;
          }
        } catch (err) {
          // ignore — try next collection
        }
      }
    };

    findCollection();
  }, [eventId, collectionProp]);

  // Second: Subscribe to real-time updates once we know the collection.
  // rumbleTotalCents / rumbleCount / floodContributions are written by the
  // backend after every completed Rumble (one-tap charge or embedded-checkout
  // webhook), so the meter refreshes live without polling. Legacy flood*
  // fields are summed in (eventRumbleTotals).
  useEffect(() => {
    if (!eventId || !foundCollection) return;
    seenContributionIdsRef.current = null;

    // Subscribe to event document for the meter totals
    const docRef = doc(db, foundCollection, eventId);
    const eventUnsub = onSnapshot(
      docRef,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          const { totalCents: newTotal, count: newCount } = eventRumbleTotals(data);

          // Check for milestone hit. prevTotalRef > 0 keeps the first snapshot
          // after mount from bursting (it seeds prev below); the shared claim
          // dedupes against EventWatchScreen's always-mounted watcher.
          if (prevTotalRef.current > 0) {
            const claimed = claimRumbleMilestone(eventId, prevTotalRef.current, newTotal);
            if (claimed) {
              setRumbleMilestone(claimed);
              setShowRumbleBurst(true);
            }
          }

          // Pulse animation on new contribution
          if (newTotal > prevTotalRef.current && prevTotalRef.current > 0) {
            Animated.sequence([
              Animated.timing(pulseAnim, { toValue: 1.1, duration: 150, useNativeDriver: true }),
              Animated.timing(pulseAnim, { toValue: 1, duration: 150, useNativeDriver: true }),
            ]).start();
          }

          prevTotalRef.current = newTotal;
          setMeterTotal(newTotal);
          setMeterCount(newCount);
        }
      },
      (error) => {
        console.error(`TsunamiMeter: Subscription error for ${foundCollection}/${eventId}:`, error);
      }
    );

    // Subscribe to recent contributions
    const contributionsUnsub = onSnapshot(
      query(
        collection(db, foundCollection, eventId, 'floodContributions'),
        orderBy('createdAt', 'desc'),
        limit(10)
      ),
      (snap) => {
        const contributions = snap.docs.map(d => ({
          id: d.id,
          ...d.data(),
        }));

        // Detect new contributions against the ids already seen (a ref — the
        // state captured by this listener is stale). The first snapshot
        // seeds silently.
        const seen = seenContributionIdsRef.current;
        const newIds = new Set();
        if (seen) {
          contributions.forEach(c => {
            if (!seen.has(c.id)) {
              newIds.add(c.id);
            }
          });
        }
        seenContributionIdsRef.current = new Set(contributions.map(c => c.id));

        setNewContributionIds(newIds);
        setRecentContributions(contributions);

        // Clear new flags after animation
        setTimeout(() => setNewContributionIds(new Set()), 500);
      },
      (error) => {
        // floodContributions subcollection may not exist yet — non-fatal
      }
    );

    return () => {
      eventUnsub();
      contributionsUnsub();
    };
  }, [eventId, foundCollection]);

  // Animate progress bar
  useEffect(() => {
    Animated.timing(progressAnim, {
      toValue: progressPercent,
      duration: 500,
      useNativeDriver: false,
    }).start();
  }, [progressPercent]);

  // ------------------------------------------------------------------
  // Embedded Stripe checkout mount/teardown
  // ------------------------------------------------------------------
  const closeEmbedded = useCallback(() => {
    // The mount effect's cleanup destroys the Stripe instance.
    setEmbedded(null);
  }, []);

  // Paid: the webhook records the Rumble and the live listener moves the
  // meter. Refresh setup so the now-saved card makes the next one one-tap
  // (and today's allowance shrinks).
  checkoutCompleteRef.current = () => {
    const held = embedded && embedded.cardCheck === true && embedded.purchaseId ? embedded.purchaseId : null;
    setEmbedded(null);
    setCustomAmount('');
    if (!held) {
      setNotice({ tone: 'ok', text: 'Rumble sent!' });
      refreshSetupRef.current?.();
      onContributeRef.current?.();
      return;
    }
    // Card checks on ('limits' mode): the payment is only authorized until
    // the server checks the card (3.7), so confirm it before saying sent.
    // The spinner holds the picker meanwhile.
    const seq = sendSeqRef.current;
    const alive = () => sendSeqRef.current === seq;
    processingRef.current = true;
    setIsProcessing(true);
    setNotice({ tone: 'info', text: CONFIRMING_TEXT });
    pollTipPurchaseOutcome('rumble', held, { isAlive: alive })
      .then(({ status, reason }) => {
        processingRef.current = false;
        if (mountedRef.current) setIsProcessing(false);
        refreshSetupRef.current?.();
        if (!alive()) return;
        if (status === 'completed') {
          setNotice({ tone: 'ok', text: 'Rumble sent!' });
          onContributeRef.current?.();
          return;
        }
        if (status === 'failed') {
          const line = captureOutcomeLine(reason, { verify: setup?.limit?.verify });
          if (line) { setNotice(null); setOutcomeLine(line); } else setNotice({ tone: 'error', text: FAILED_TEXT });
          return;
        }
        setNotice({ tone: 'info', text: STILL_CONFIRMING_TEXT });
      });
  };
  checkoutErrorRef.current = () => {
    setEmbedded(null);
    setNotice({ tone: 'error', text: 'Could not open the payment form. Please try again.' });
  };

  // Mount the embedded checkout whenever we have a clientSecret. Keyed on
  // `embedded` ONLY: callbacks arrive through refs (see above).
  useEffect(() => {
    if (!IS_WEB || !embedded?.clientSecret) return undefined;
    return mountEmbeddedCheckout(
      checkoutContainerRef,
      { clientSecret: embedded.clientSecret, publishableKey: embedded.publishableKey },
      checkoutCompleteRef,
      checkoutErrorRef,
    );
  }, [embedded]);

  // Picker range + presets from setup (server-authoritative; the event minimum
  // and today's remaining allowance are already applied there).
  const minCents = positiveOr(setup?.minCents, DEFAULT_MIN_CENTS);
  const maxCents = positiveOr(setup?.maxCents, DEFAULT_MAX_CENTS);
  const presets = useMemo(() => {
    const src = Array.isArray(setup?.presetsCents) && setup.presetsCents.length ? setup.presetsCents : QUICK_AMOUNTS;
    const out = [];
    src.forEach((p) => {
      const c = Math.round(Number(p) || 0);
      if (c >= minCents && c <= maxCents && !out.includes(c)) out.push(c);
    });
    return out;
  }, [setup, minCents, maxCents]);
  const closedReason = closedReasonFor(setup);

  // 'limits' mode only ('cap' sends no limit object): the account's room, and
  // whether the saved card can be used here. At the limit the closed note is
  // the numbered line (L3, L3b, L3p, L4) instead of the old sentence.
  const limit = setup?.limit && typeof setup.limit === 'object' ? setup.limit : null;
  const savedStatus = savedCardStatus(limit);
  const limitClosed = !!limit && setup?.reason === 'tips-limit' && !!limitLines(limit)?.atLimit;

  // ------------------------------------------------------------------
  // Send a Rumble — real money via Stripe (utils/rumblePayment.js)
  // ------------------------------------------------------------------
  // opts.skipOneTap: "Use another card" goes straight to the embedded
  // checkout, as does a saved card the setup says can't be used here.
  const handleContribute = useCallback(async (amountCents, opts) => {
    if (processingRef.current) return;

    // Must be logged in (real money).
    if (!loggedIn) {
      if (onRequireAuthRef.current) { onRequireAuthRef.current(); return; }
      const message = 'Please log in to send a Rumble.';
      if (IS_WEB && typeof window !== 'undefined') window.alert(message);
      else Alert.alert('Login Required', message, [{ text: 'OK' }]);
      return;
    }

    if (!IS_WEB) {
      Alert.alert('Web only', 'Rumbles are available on the web app.', [{ text: 'OK' }]);
      return;
    }

    if (!rumblesOpen || !rumbleTarget || !lockKey) {
      setNotice({ tone: 'info', text: closedReason });
      return;
    }

    // One send at a time per target across every meter (another meter's send,
    // or this one's from before a close, may still be running), and none for a
    // minute after an unknown one-tap outcome: a second tap could charge twice.
    if (rumbleSendBlocked(lockKey)) {
      setNotice({ tone: 'info', text: PENDING_TEXT });
      return;
    }

    // Out of range: say so rather than silently charging a different amount.
    const amt = Math.round(Number(amountCents) || 0);
    if (!amt || amt < minCents || amt > maxCents) {
      // 'limits' mode: an amount above the room gets L2 with the room.
      if (limit && limit.limited !== false && amt >= minCents && amt > (Number(limit.remainingCents) || 0)) {
        setNotice(null);
        setLimitErr({ limit, amountCents: amt });
        return;
      }
      setNotice({ tone: 'error', text: `Choose an amount from ${formatUsd(minCents)} to ${formatUsd(maxCents)}.` });
      return;
    }

    const lock = rumbleSendLock(lockKey);
    const seq = sendSeqRef.current;
    const alive = () => sendSeqRef.current === seq;
    // Parent-owned callbacks still run after a close; this meter's own setup
    // hook (standalone meter) only while it is mounted.
    const refreshSetup = () => { if (parentOwnsSetup || alive()) refreshSetupRef.current?.(); };
    const skipOneTap = !!(opts && opts.skipOneTap === true) || savedStatus !== 'ok';
    processingRef.current = true;
    lock.inFlight = true;
    setIsProcessing(true);
    setNotice(null);
    setLimitErr(null);
    setOutcomeLine(null);
    let stage = 'onetap';
    try {
      if (setup?.hasSavedCard && !skipOneTap) {
        // One-tap off-session charge on the saved card (no redirect, no form).
        try {
          await sendRumbleOneTap(rumbleTarget, amt);
          if (alive()) {
            setCustomAmount('');
            setNotice({ tone: 'ok', text: 'Rumble sent!' });
          }
          refreshSetup();
          onContributeRef.current?.();
          return;
        } catch (err) {
          // ONLY SCA / no usable saved card falls back to the embedded
          // checkout. A pending outcome may already have charged the card,
          // and a closed target or today's limit must never become a checkout.
          // A card refusal ('card') waits for "Use another card".
          if (rumbleErrorKind(err) !== 'checkout') throw err;
        }
      }
      // Closed mid-send: no checkout nobody would see (its pending purchase
      // would count against today's limit until it expires).
      if (!alive()) return;
      stage = 'checkout';
      // First-time payer (or SCA) — embedded checkout (also saves the card).
      const data = await createRumbleCheckout(rumbleTarget, amt);
      if (!alive()) return;
      setCardRefusal(null);
      setEmbedded({
        clientSecret: data.clientSecret,
        publishableKey: data.publishableKey || setup?.publishableKey || null,
        cardCheck: data.cardCheck === true,
        purchaseId: typeof data.purchaseId === 'string' ? data.purchaseId : null,
      });
    } catch (err) {
      const kind = rumbleErrorKind(err);
      console.warn('TsunamiMeter: Rumble failed:', kind, err?.code || err?.message);
      // Unknown one-tap outcome: lock this target for a minute, even if this
      // meter has closed (the lock lives at module level).
      if (kind === 'pending' && stage === 'onetap') lock.pendingUntil = Date.now() + PENDING_LOCK_MS;
      if (alive()) {
        const lim = kind === 'limit' ? limitFromError(err) : null;
        const cardStatus = kind === 'card' ? cardStatusFromError(err) : null;
        if (lim) setLimitErr({ limit: lim, amountCents: amt });
        else if (cardStatus) setCardRefusal({ status: cardStatus, amountCents: amt });
        else {
          // A transport failure while starting checkout charges nothing.
          const text = kind === 'pending' && stage === 'checkout'
            ? CHECKOUT_FAILED_TEXT
            : rumbleNoticeText(err, kind);
          setNotice({ tone: kind === 'pending' && stage === 'onetap' ? 'info' : 'error', text });
        }
      }
      // Closed / limit: re-read setup so the picker hides with its reason.
      if (kind === 'unavailable' || kind === 'limit') refreshSetup();
    } finally {
      processingRef.current = false;
      lock.inFlight = false;
      // Still mounted (even on another target): the spinner must clear.
      if (mountedRef.current) setIsProcessing(false);
    }
  }, [loggedIn, rumblesOpen, rumbleTarget, lockKey, parentOwnsSetup, closedReason, minCents, maxCents, setup]);

  // Handle custom amount submission (dollars, the payer's own picker)
  const handleCustomSubmit = useCallback(() => {
    const dollars = parseFloat(String(customAmount).replace(/[$,\s]/g, ''));
    if (!Number.isFinite(dollars) || dollars <= 0) {
      setNotice({ tone: 'error', text: `Choose an amount from ${formatUsd(minCents)} to ${formatUsd(maxCents)}.` });
      return;
    }
    handleContribute(Math.round(dollars * 100));
  }, [customAmount, handleContribute, minCents, maxCents]);

  // The picker shows only while Rumbles are open for this viewer + event
  // (setup tipsEnabled). It stays visible while the checkout overlay is open
  // (the overlay floats above it).
  const showActions = IS_WEB && loggedIn && rumblesOpen;
  const showClosedNote = IS_WEB && loggedIn && !setupLoading && !rumblesOpen;

  return (
    <View style={[styles.container, compact && styles.containerCompact]}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.titleRow}>
          <DieIcon size={20} />
          <Text style={styles.title}>RUMBLE METER</Text>
        </View>
        <Text style={styles.waveCount}>Level {meterLevel}</Text>
      </View>

      {/* Rumble count — fan-facing, so a count and never an amount */}
      <Animated.View style={[styles.totalContainer, { transform: [{ scale: pulseAnim }] }]}>
        <Text style={[styles.totalAmount, compact && styles.totalAmountCompact]}>
          {meterCount.toLocaleString('en-US')}
        </Text>
        <Text style={styles.totalLabel}>
          {meterCount === 1 ? 'Rumble from the crowd' : 'Rumbles from the crowd'}
        </Text>
      </Animated.View>

      {/* Progress Bar */}
      <View style={styles.progressContainer}>
        <View style={styles.progressBg}>
          <Animated.View
            style={[
              styles.progressBar,
              {
                width: progressAnim.interpolate({
                  inputRange: [0, 100],
                  outputRange: ['0%', '100%'],
                }),
              },
            ]}
          >
            {/* Purple die rides the leading edge — contrasts against the gold fill. */}
            <Image source={DIE_P20} style={styles.progressDie} resizeMode="contain" />
          </Animated.View>
        </View>
      </View>

      {/* Level Labels */}
      <View style={styles.milestoneLabels}>
        <Text style={[styles.milestoneLabel, styles.milestoneLabelReached]}>
          LV {meterLevel}
        </Text>
        <Text style={[styles.milestoneLabel, styles.milestoneLabelNext]}>
          LV {meterLevel + 1}
        </Text>
      </View>

      {/* Target Info */}
      <View style={styles.targetContainer}>
        <Text style={styles.targetLabel}>NEXT RUMBLE AT</Text>
        <Text style={styles.targetAmount}>Level {meterLevel + 1}</Text>
        <Text style={styles.targetRemaining}>
          {Math.floor(progressPercent)}% of the way there
        </Text>
      </View>

      {/* Framing (money-free) */}
      <Text style={styles.floodFraming}>
        Boost this event and cheer on the players.
      </Text>

      {/* Picker — only while Rumbles are open for this viewer + event. The
          backend validates + charges. The Stripe form for first-time payers
          opens in a floating overlay (below), so these controls stay in place
          and the live stream behind is never resized. Amounts show here only:
          this is the payer's own picker. */}
      {showActions && (
        <>
          <View style={styles.buttonsContainer}>
            {presets.map((amount, index) => (
              <Pressable
                key={amount}
                style={[
                  styles.amountButton,
                  index === 1 && styles.amountButtonPopular, // middle preset highlighted
                  isProcessing && styles.amountButtonDisabled,
                ]}
                onPress={() => handleContribute(amount)}
                disabled={isProcessing}
              >
                <Text
                  style={[
                    styles.amountButtonText,
                    index === 1 && styles.amountButtonTextPopular,
                  ]}
                >
                  {formatUsd(amount)}
                </Text>
                <Text style={[styles.amountButtonSub, index === 1 && styles.amountButtonTextPopular]}>
                  {waveLabel(rumbleWave(amount))}
                </Text>
              </Pressable>
            ))}
          </View>

          {/* Custom Amount (dollars) */}
          <View style={styles.customContainer}>
            <TextInput
              style={styles.customInput}
              placeholder={`Custom ${formatUsd(minCents)}–${formatUsd(maxCents)}`}
              placeholderTextColor="#555"
              keyboardType="decimal-pad"
              value={customAmount}
              onChangeText={setCustomAmount}
              editable={!isProcessing}
            />
            <Pressable
              style={[styles.customButton, isProcessing && styles.customButtonDisabled]}
              onPress={handleCustomSubmit}
              disabled={isProcessing}
            >
              {isProcessing ? (
                <ActivityIndicator size="small" color="#000" />
              ) : (
                <Text style={styles.customButtonText}>Rumble</Text>
              )}
            </Pressable>
          </View>

          {/* 'limits' mode: the room under the amounts (L1); a refusal's
              own numbers replace it (below). */}
          {limit && !limitErr && (
            <SpendingLimitNotice limit={limit} onExpire={() => refreshSetupRef.current?.()} onVerify={onVerify} />
          )}

          {/* Saved-card hint ('limits' mode: the card line when the saved
              card can't be used here; the amounts then open checkout). No
              "Use another card" button on this line: the meter has no
              selected amount to send, so the amount buttons above are the
              way to another card (a recorded deviation from 5.14 L5s / L6) */}
          {!setupLoading && setup?.hasSavedCard && setup?.card && savedStatus === 'ok' && (
            <Text style={styles.savedCardNote}>
              One-tap with {setup.card.brand} ···· {setup.card.last4}
            </Text>
          )}
          {!setupLoading && setup?.hasSavedCard && savedStatus !== 'ok' && !cardRefusal && (
            <SpendingLimitNotice
              line={cardLine(savedStatus, { saved: true, verify: limit?.verify })}
              tone="error"
              onVerify={onVerify}
            />
          )}
        </>
      )}

      {/* Purchase limits ('limits' mode only): a refusal's numbers, a card
          refusal with "Use another card", a held checkout's outcome. */}
      {IS_WEB && loggedIn && limitErr && (
        <SpendingLimitNotice
          limit={limitErr.limit}
          amountCents={limitErr.amountCents}
          onExpire={() => { setLimitErr(null); refreshSetupRef.current?.(); }}
          onVerify={onVerify}
        />
      )}
      {IS_WEB && loggedIn && cardRefusal && (
        <SpendingLimitNotice
          line={cardLine(cardRefusal.status, { verify: limit?.verify })}
          tone="error"
          onUseAnotherCard={rumblesOpen ? () => handleContribute(cardRefusal.amountCents, { skipOneTap: true }) : undefined}
          onVerify={onVerify}
          disabled={isProcessing}
        />
      )}
      {IS_WEB && loggedIn && outcomeLine && (
        <SpendingLimitNotice line={outcomeLine} tone="error" onVerify={onVerify} />
      )}

      {/* Not open for this logged-in viewer (closed, finished, today's limit,
          flag off): say why instead of showing the picker. */}
      {showClosedNote && !limitClosed && (
        <Text style={styles.unavailableNote}>{closedReason}</Text>
      )}
      {showClosedNote && limitClosed && !limitErr && (
        <SpendingLimitNotice limit={limit} onExpire={() => refreshSetupRef.current?.()} onVerify={onVerify} />
      )}

      {/* Outcome of the last send (payer-facing, money-free) */}
      {notice && !(showClosedNote && notice.text === closedReason) && (
        <Text
          style={[
            styles.noticeText,
            notice.tone === 'ok' && styles.noticeOk,
            notice.tone === 'error' && styles.noticeError,
          ]}
        >
          {notice.text}
        </Text>
      )}

      {/* Logged-out funnel — only while Rumbles are open on this event
          (handleContribute routes a logged-out tap to sign-up) */}
      {IS_WEB && !loggedIn && rumblesOpen && (
        <Pressable
          style={styles.signupCta}
          onPress={() => handleContribute(presets[0] || DEFAULT_MIN_CENTS)}
        >
          <Text style={styles.signupCtaText}>Sign up to Rumble</Text>
        </Pressable>
      )}

      {/* Native fallback */}
      {!IS_WEB && (
        <Text style={styles.unavailableNote}>Rumbles are available on the web app.</Text>
      )}

      {/* Floating payment overlay — the Stripe embedded checkout mounts here,
          centered above the (still-playing) stream behind a dim scrim. */}
      {IS_WEB && embedded && (
        <Modal visible transparent animationType="fade" onRequestClose={closeEmbedded}>
          <View style={styles.checkoutOverlay}>
            <Pressable style={styles.checkoutScrim} onPress={closeEmbedded} />
            <View style={styles.checkoutCard}>
              <View style={styles.checkoutHeader}>
                <Text style={styles.checkoutTitle}>Send a Rumble</Text>
                <Pressable onPress={closeEmbedded} hitSlop={8}>
                  <Text style={styles.checkoutClose}>✕</Text>
                </Pressable>
              </View>
              <ScrollView style={styles.checkoutScroll} showsVerticalScrollIndicator={false}>
                {/* RN Web renders raw DOM nodes; Stripe mounts the embedded
                    checkout iframe into this div. */}
                <div ref={checkoutContainerRef} style={{ width: '100%' }} />
              </ScrollView>
            </View>
          </View>
        </Modal>
      )}

      {/* Recent Contributions */}
      {showContributions && recentContributions.length > 0 && (
        <View style={styles.recentContainer}>
          <Text style={styles.recentTitle}>RECENT RUMBLES</Text>
          {recentContributions.slice(0, 5).map((contribution) => (
            <ContributionItem
              key={contribution.id}
              contribution={contribution}
              isNew={newContributionIds.has(contribution.id)}
            />
          ))}
        </View>
      )}

      {/* Tsunami Alert Modal */}
      <RumbleBurst
        visible={showRumbleBurst}
        milestone={rumbleMilestone}
        subtitle={rumbleMilestone ? `The crowd hit Level ${rumbleMeterLevel(rumbleMilestone)}!` : undefined}
        onDone={() => setShowRumbleBurst(false)}
      />
    </View>
  );
}

// ============================================
// COMPACT TSUNAMI INDICATOR (for event cards)
// ============================================
// Money-free: flags that the crowd has Rumbled, never how much.
export function TsunamiIndicator({ floodTotalCents }) {
  if (!floodTotalCents || floodTotalCents <= 0) return null;

  return (
    <View style={styles.indicatorContainer}>
      <WaveIconSVG size={12} color="#4ade80" />
      <Text style={styles.indicatorAmount}>
        Rumbled by the crowd
      </Text>
    </View>
  );
}

// ============================================
// STYLES
// ============================================
const styles = StyleSheet.create({
  container: {
    backgroundColor: '#111',
    borderRadius: 16,
    padding: 16,
    borderWidth: 1,
    borderColor: '#222',
  },
  containerCompact: {
    padding: 12,
  },

  // Header
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  titleIcon: {
    fontSize: 16,
  },
  title: {
    fontSize: 12,
    fontWeight: '800',
    color: '#00bfff',
    letterSpacing: 0.5,
  },
  waveCount: {
    fontSize: 12,
    fontWeight: '700',
    color: '#4ade80',
  },

  // Total
  totalContainer: {
    alignItems: 'center',
    marginBottom: 16,
  },
  totalAmount: {
    fontSize: 38,
    fontWeight: '900',
    color: '#fff',
  },
  totalAmountCompact: {
    fontSize: 28,
  },
  totalLabel: {
    fontSize: 12,
    color: '#666',
    marginTop: 2,
  },

  // Progress Bar
  progressContainer: {
    marginBottom: 8,
  },
  progressBg: {
    height: 20,
    backgroundColor: '#1a1a1a',
    borderRadius: 10,
    overflow: 'hidden',
  },
  progressBar: {
    height: '100%',
    backgroundColor: '#FCD000',   // gold fill
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'flex-end',
    paddingRight: 4,
  },
  progressDie: {
    width: 18,
    height: 18,
  },
  progressWave: {
    fontSize: 14,
  },

  // Milestones
  milestoneLabels: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  milestoneLabel: {
    fontSize: 10,
    fontWeight: '600',
    color: '#444',
  },
  milestoneLabelReached: {
    color: '#00bfff',
  },
  milestoneLabelNext: {
    color: '#FCD000',
    fontWeight: '800',
  },

  // Target
  targetContainer: {
    alignItems: 'center',
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#222',
    marginBottom: 16,
  },
  targetLabel: {
    fontSize: 10,
    fontWeight: '700',
    color: '#666',
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  targetAmount: {
    fontSize: 18,
    fontWeight: '800',
    color: '#FCD000',
  },
  targetRemaining: {
    fontSize: 12,
    color: '#888',
    marginTop: 4,
  },

  // Rising-tides framing line
  floodFraming: {
    fontSize: 12,
    color: '#9aa',
    textAlign: 'center',
    marginBottom: 12,
    paddingHorizontal: 4,
  },

  // Embedded checkout
  embeddedWrap: {
    marginBottom: 8,
  },
  embeddedHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  embeddedTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#fff',
  },
  embeddedCancel: {
    fontSize: 13,
    fontWeight: '700',
    color: '#888',
  },

  // Floating payment overlay (desktop): dim scrim + centered card; the stream
  // keeps playing behind it (never resized).
  checkoutOverlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  checkoutScrim: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.75)',
  },
  checkoutCard: {
    width: '100%',
    maxWidth: 460,
    maxHeight: '88%',
    backgroundColor: '#0f0f12',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#2a2a30',
    padding: 16,
    shadowColor: '#000',
    shadowOpacity: 0.5,
    shadowRadius: 30,
    shadowOffset: { width: 0, height: 12 },
  },
  checkoutHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  checkoutTitle: {
    fontSize: 16,
    fontWeight: '800',
    color: '#fff',
  },
  checkoutClose: {
    fontSize: 18,
    fontWeight: '800',
    color: '#999',
    paddingHorizontal: 4,
  },
  checkoutScroll: {
    width: '100%',
  },

  // Saved-card / availability notes
  savedCardNote: {
    fontSize: 11,
    color: '#4ade80',
    textAlign: 'center',
    marginTop: 4,
  },
  unavailableNote: {
    fontSize: 12,
    color: '#888',
    textAlign: 'center',
    marginTop: 4,
    fontStyle: 'italic',
  },
  noticeText: {
    fontSize: 12,
    color: '#cbd5e1',
    textAlign: 'center',
    marginTop: 8,
  },
  noticeOk: {
    color: '#4ade80',
    fontWeight: '700',
  },
  noticeError: {
    color: '#f87171',
  },

  // Signup CTA (logged-out)
  signupCta: {
    paddingVertical: 14,
    borderRadius: 8,
    backgroundColor: '#FCD000',
    alignItems: 'center',
  },
  signupCtaText: {
    fontSize: 14,
    fontWeight: '800',
    color: '#000',
  },

  // Buttons
  buttonsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 12,
  },
  amountButton: {
    flexBasis: '31%',
    flexGrow: 1,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: '#1a1a1a',
    borderWidth: 1,
    borderColor: '#333',
    alignItems: 'center',
  },
  amountButtonPopular: {
    backgroundColor: '#00bfff',
    borderColor: '#00bfff',
  },
  amountButtonDisabled: {
    opacity: 0.5,
  },
  amountButtonText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#fff',
  },
  amountButtonSub: {
    fontSize: 11,
    fontWeight: '700',
    color: '#cbd5e1',
    marginTop: 1,
  },
  amountButtonTextPopular: {
    color: '#000',
  },
  amountButtonTextDisabled: {
    color: '#555',
  },

  // Custom Amount
  customContainer: {
    flexDirection: 'row',
    gap: 8,
  },
  customInput: {
    flex: 1,
    height: 44,
    backgroundColor: '#111',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#333',
    paddingHorizontal: 12,
    color: '#fff',
    fontSize: 14,
  },
  customButton: {
    height: 44,
    paddingHorizontal: 20,
    backgroundColor: '#FCD000',
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  customButtonDisabled: {
    opacity: 0.5,
  },
  customButtonText: {
    fontSize: 14,
    fontWeight: '800',
    color: '#000',
  },

  // Recent Contributions
  recentContainer: {
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#222',
  },
  recentTitle: {
    fontSize: 10,
    fontWeight: '700',
    color: '#666',
    letterSpacing: 0.5,
    marginBottom: 10,
  },
  contributionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#1a1a1a',
  },
  contributionIcon: {
    fontSize: 14,
    marginRight: 8,
  },
  contributionDie: {
    width: 18,
    height: 18,
    marginRight: 8,
  },
  contributionAvatar: {
    width: 18,
    height: 18,
    borderRadius: 9,
    marginRight: 6,
    backgroundColor: '#222',
  },
  contributionUser: {
    flex: 1,
    fontSize: 13,
    color: '#ddd',
    fontWeight: '600',
  },
  contributionAmount: {
    fontSize: 13,
    fontWeight: '700',
    marginRight: 8,
  },
  contributionTime: {
    fontSize: 10,
    color: '#444',
  },

  // Tsunami Alert
  tsunamiOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  tsunamiContent: {
    alignItems: 'center',
  },
  tsunamiIcon: {
    fontSize: 100,
  },
  tsunamiTitle: {
    fontSize: 48,
    fontWeight: '900',
    color: '#00bfff',
    marginTop: 16,
    textShadowColor: 'rgba(0, 191, 255, 0.8)',
    textShadowOffset: { width: 0, height: 0 },
    textShadowRadius: 20,
  },
  tsunamiAmount: {
    fontSize: 24,
    fontWeight: '700',
    color: '#4ade80',
    marginTop: 12,
  },
  tsunamiNext: {
    fontSize: 16,
    color: '#FCD000',
    marginTop: 12,
  },

  // Compact Indicator
  indicatorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: 4,
  },
  indicatorIcon: {
    fontSize: 12,
  },
  indicatorAmount: {
    fontSize: 11,
    fontWeight: '600',
    color: '#4ade80',
  },
});
